import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useProjectContentItemElementsMutation } from 'components/WorkspaceEditor/api/mutations/projectContentItemElements'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { ParagraphInputBuilder } from 'components/WorkspaceEditor/factories'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types/types'
import { getRootArticleContentItemIds } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import {
    useShallowSelector,
    useWorkspace2Store,
} from 'components/WorkspaceEditor/Workspace2Store'
import { useCallback, useRef } from 'react'

/**
 * This block calls addProjectContentItem api before setProjectContentItemElements
 * call is made only on new paragraphs. We need dtvItemId from addProjectContentItem
 * to save other elements after it using setProjectContentItemElements

 * This block won't execute for already saved paragraphs/base contents as they already 
 * have dtvItemId
 */
export const useEnsureParagraphSaved = () => {
    const {
        familyAddedProductTypes,
        selectedProjectElementNodeId,
        setContentItemViewNodes,
    } = useShallowSelector((state) => ({
        familyAddedProductTypes: state.familyAddedProductTypes,
        selectedProjectElementNodeId: state.selectedProjectElementNodeId,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
    }))

    const { elementType } = useElementItemShallowSelector(
        (s) => s.elementItemValue
    )

    const { addProjectContentItem } = useProjectContentItemElementsMutation()

    const pendingParagraphClientIdRef = useRef<string | null>(null)
    const pendingResolveRef = useRef<((success: boolean) => void) | null>(null)
    const preAddIdsRef = useRef<Set<string>>(new Set())

    // Serializes concurrent ensureParentParagraphSaved calls — refs are single-slot,
    // so a second in-flight ADD+GET would overwrite pendingParagraphClientIdRef and the
    // first promise would never resolve.
    const pendingPromiseRef = useRef<Promise<void> | null>(null)

    // Keep latest callback body in a ref so the stable wrapper below never changes
    // identity and does not trigger useProductFetcher to re-register.
    const callbackBodyRef = useRef<
        (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => Promise<void>
    >()
    callbackBodyRef.current = async (response) => {
        const clientGuid = pendingParagraphClientIdRef.current
        if (!clientGuid) return

        const responseNodes =
            (response as GetProjectProductFamilyViewResponse)
                .getProjectProductFamilyView?.contentItemViewNodes ??
            (response as GetProjectNuggetContentViewResponse).getProjectNugget
                ?.contentItemViewNodes ??
            []

        const preAddIds = preAddIdsRef.current
        const serverNode = responseNodes.find(
            (n) => n.id && !preAddIds.has(n.id)
        ) as Partial<ContentItemViewNodesType> | undefined

        pendingParagraphClientIdRef.current = null
        preAddIdsRef.current = new Set()

        if (serverNode) {
            const storeNodes = useWorkspace2Store.getState()
                .contentItemViewNodes
            const patched = storeNodes.map((node) => {
                if (node.id !== clientGuid) return node

                const updatedNode: Partial<ContentItemViewNodesType> = {
                    ...node,
                    id: serverNode.id!,
                    dtvItemId: serverNode.dtvItemId,
                    propertyRefId: serverNode.propertyRefId,
                    contentItemOwnerId: serverNode.contentItemOwnerId,
                    contentItemOwnerType: serverNode.contentItemOwnerType,
                }

                // Replace client-generated Text element IDs with server-assigned IDs.
                // OptionGroup/SelectionOption/UOM keep their client IDs (new, unknown to server).
                const serverElements = serverNode.elements ?? []
                if (serverElements.length > 0) {
                    let serverIdx = 0
                    updatedNode.elements = node.elements?.map((el) => {
                        if (
                            el.type === 'Text' &&
                            serverElements[serverIdx]?.id
                        ) {
                            const serverId = serverElements[serverIdx].id!
                            serverIdx++
                            return { ...el, id: serverId }
                        }
                        return el
                    })
                }

                return updatedNode
            })
            setContentItemViewNodes(
                patched as Partial<ContentItemViewNodesType>[]
            )
            pendingResolveRef.current?.(true)
        } else {
            pendingResolveRef.current?.(false)
        }
        pendingResolveRef.current = null
    }

    const onErrorBodyRef = useRef<() => void>()
    onErrorBodyRef.current = () => {
        pendingParagraphClientIdRef.current = null
        preAddIdsRef.current = new Set()
        pendingResolveRef.current?.(false)
        pendingResolveRef.current = null
    }

    // Stable wrappers: identity never changes, so useProductFetcher never re-registers.
    const stableCallback = useCallback(
        (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => callbackBodyRef.current!(response),
        []
    )
    const stableOnError = useCallback(() => onErrorBodyRef.current!(), [])

    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: stableCallback,
        onError: stableOnError,
    })

    /**
     * If the paragraph containing `elementId` is unsaved (dtvItemId absent), saves it via
     * addProjectContentItem then GETs to populate the store with server-assigned ids.
     * Throws on failure. No-ops when the paragraph is already in the DB.
     */
    const ensureParentParagraphSaved = useCallback(
        async (elementId: string): Promise<void> => {
            // Serialize: a second call while ADD+GET is in flight waits for the first to
            // finish (paragraph will be in DB when it resolves).
            if (pendingPromiseRef.current) {
                await pendingPromiseRef.current
                return
            }

            const currentNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            const findById = (
                element: Partial<TextContentElement>,
                id: string
            ): boolean =>
                element.id === id ||
                (element.elements?.some((c) => findById(c, id)) ?? false)

            const paragraphNode = currentNodes.find((node) =>
                node.elements?.some((el) => findById(el, elementId))
            )

            if (!paragraphNode || paragraphNode.dtvItemId) return
            const paragraphId = paragraphNode.id
            if (!paragraphId) return

            const textElement = paragraphNode.elements?.find(
                (el) => el.type === 'Text'
            )
            const variables = new ParagraphInputBuilder(
                textElement?.text ?? '',
                paragraphId,
                currentNodes,
                familyAddedProductTypes,
                selectedProjectElementNodeId,
                elementType as ProjectElementType
            ).build()

            const rootIds = getRootArticleContentItemIds(currentNodes, [
                textElement?.id ?? '',
            ])

            preAddIdsRef.current = new Set(
                currentNodes.map((n) => n.id).filter(Boolean) as string[]
            )

            // Register pending promise before first await so concurrent callers see it.
            let resolvePending!: () => void
            let rejectPending!: (e: unknown) => void
            pendingPromiseRef.current = new Promise<void>((res, rej) => {
                resolvePending = res
                rejectPending = rej
            })
            // Suppress unhandled-rejection when no concurrent caller is awaiting this promise.
            pendingPromiseRef.current.catch(() => {})

            try {
                await addProjectContentItem({ variables })

                const success = await new Promise<boolean>((resolve) => {
                    pendingParagraphClientIdRef.current = paragraphId
                    pendingResolveRef.current = resolve
                    getContentItemViewNodesWithApolloClient(rootIds)
                })

                if (!success) {
                    throw new Error(
                        'Failed to retrieve server ids after addProjectContentItem'
                    )
                }
                resolvePending()
            } catch (e) {
                rejectPending(e)
                throw e
            } finally {
                pendingPromiseRef.current = null
            }
        },
        [
            addProjectContentItem,
            familyAddedProductTypes,
            selectedProjectElementNodeId,
            elementType,
            getContentItemViewNodesWithApolloClient,
        ]
    )

    return { ensureParentParagraphSaved }
}
